import { useCallback } from "react";

import isNil from "lodash/isNil";

import { AnalyticsComponent } from "@/constants/analytics/Analytics";
import { ScheduleAnalyticsSources } from "@/constants/analytics/Scheduling";
import { ModalPath } from "@/constants/Navigation";
import type { ScheduleEventDetailsSubDeepLinks } from "@/constants/Schedule";
import { router } from "@/crossPlatform/expo-router";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import { useToast } from "@/hooks/useToast";
import useAppMonitor from "@/services/AppMonitor";
import { useScheduleEventDetailsModalDispatch } from "@/store/reducers/modals/scheduleEventDetailsModal";
import { getScheduleEventProps } from "@/utils/schedule.util";

interface UseScheduleEventDetailsSubDeepLinkConfig<
  T extends AppointmentDetailResponse | GroupClassEventResponse,
> {
  /** Analytics event name to track when the deep link fires */
  analyticsEvent: string;
  /** Appointment metadata to attach to the modal (undefined for group class events) */
  appointmentMetaData: AppointmentDetailResponse | undefined;
  /** The fetched event, undefined while loading or on error */
  data: T | undefined;
  /** The query error, forwarded to the captured exception on load failure */
  error: unknown;
  /** Human-readable label for the event type, used in the error toast/exception */
  errorLabel: string;
  /** Whether the underlying query is still fetching */
  isFetching: boolean;
  /** Whether `isGroupClassEvent` should be set on the event details payload */
  isGroupClassEvent: boolean;
  /** Modal title to display */
  modalTitle: string;
  /** The URL query param key holding the event id (cleared after navigating) */
  paramKey: "appointmentExternalId" | "groupClassEventExternalId";
  /** The value of that query param, forwarded to the captured exception on failure */
  paramValue: string | undefined;
  /**
   * In-app-only sub-target to hand to the schedule-event-details modal so it can
   * auto-open a nested action (e.g. the edit bottom sheet). Not part of any URL.
   */
  subtarget?: ScheduleEventDetailsSubDeepLinks;
  /** The `target` query param value that triggers this deep link */
  targetValue: string;
}

/**
 * Shared implementation for the schedule-tab event sub-deep links (appointment,
 * edit-appointment, group class event), all of which open the
 * schedule-event-details modal. The caller owns the RTK Query call and passes
 * the fetched data plus per-type config; this hook holds the otherwise identical
 * open/error/navigation logic.
 */
const useScheduleEventDetailsSubDeepLink = <
  T extends AppointmentDetailResponse | GroupClassEventResponse,
>({
  analyticsEvent,
  appointmentMetaData,
  data,
  error,
  errorLabel,
  isFetching,
  isGroupClassEvent,
  modalTitle,
  paramKey,
  paramValue,
  subtarget,
  targetValue,
}: UseScheduleEventDetailsSubDeepLinkConfig<T>) => {
  const { showToast } = useToast();
  const { captureException } = useAppMonitor();
  const { setScheduleEventDetailsData } =
    useScheduleEventDetailsModalDispatch();

  const handleOpen = useCallback(() => {
    if (isNil(data)) {
      if (isFetching) return;
      captureException(new Error(`${errorLabel} deep link failed to load`), {
        context: { extra: { error, [paramKey]: paramValue } },
      });
      showToast({
        text: `Couldn't load this ${errorLabel}`,
        variant: "error",
      });
      return;
    }
    const eventProps = getScheduleEventProps({
      event: data,
      isGroupClassEvent,
    });
    const selectedUserId = eventProps.selectedUserId ?? "";

    setScheduleEventDetailsData({
      appointmentMetaData,
      eventDetails: {
        attendees: eventProps.attendees,
        description: eventProps.description,
        endDate: eventProps.endDate,
        externalId: eventProps.externalId,
        frequency: eventProps.frequency,
        groupClassExternalId: eventProps.groupClassExternalId,
        iconName: eventProps.iconName,
        isGroupClassEvent,
        isPhoneCallEvent: eventProps.isPhoneCallEvent,
        isRecurring: eventProps.isRecurring,
        patientExternalId: selectedUserId,
        startDate: eventProps.startDate,
        title: eventProps.title,
        typeGroup: eventProps.typeGroup || "THERAPY",
      },
      isUpcomingEvent: false,
      selectedUserId,
      title: modalTitle,
    });

    // clear the deep-link-specific params from the URL to prevent the modal re-opening on subsequent navigation
    router.setParams({
      deepLinkReceivedAt: undefined,
      [paramKey]: undefined,
      target: undefined,
    });

    // when a sub-target is set, navigate with it (plus a fresh deepLinkReceivedAt)
    // so the modal's own useSubDeepLink fires and auto-opens the nested action.
    if (isNil(subtarget)) {
      router.navigate(ModalPath.ScheduleEventDetails);
      return;
    }
    router.navigate({
      params: {
        deepLinkReceivedAt: Date.now().toString(),
        subtarget,
      },
      pathname: ModalPath.ScheduleEventDetails,
    });
  }, [
    appointmentMetaData,
    captureException,
    data,
    error,
    errorLabel,
    isFetching,
    isGroupClassEvent,
    modalTitle,
    paramKey,
    paramValue,
    setScheduleEventDetailsData,
    showToast,
    subtarget,
  ]);

  useSubDeepLink({
    analyticsEvent,
    analyticsProps: {
      component: AnalyticsComponent.Tab,
      source: ScheduleAnalyticsSources.ScheduleScreen,
    },
    onOpen: handleOpen,
    shouldHandle: !isFetching,
    targetValue,
  });
};

export default useScheduleEventDetailsSubDeepLink;
